import { BleClient, dataViewToText, textToDataView, type ScanResult } from "@capacitor-community/bluetooth-le";
import {
  RUN_CLOCK_CHAR_CREDS,
  RUN_CLOCK_CHAR_INFO,
  RUN_CLOCK_CHAR_NETS,
  RUN_CLOCK_CHAR_STATUS,
  RUN_CLOCK_SERVICE,
  credentialsPayload,
  parseInfo,
  parseNets,
  parseStatus,
  type RunClockInfo,
  type RunClockStatus,
} from "@/lib/domain/runClock";
import { isNative, platform } from "./capacitor";

/**
 * Bluetooth LE transport for setting up a Run Clock from the app (#374).
 *
 * Thin wrapper over @capacitor-community/bluetooth-le: the clock's GATT
 * service and payloads live in lib/domain/runClock.ts (pure, tested). Works
 * natively on Android and iOS (#379) and, through the plugin's Web Bluetooth
 * implementation, in Chrome on Android; `runClockBleAvailable()` gates the UI.
 *
 * iOS differences the callers see: the plugin cannot switch Bluetooth on or
 * ask for the permission a second time (`requestEnable`/`createBond` throw),
 * the pairing PIN dialog comes from the OS on the first encrypted read (the
 * network list) rather than from an explicit bond, and `deviceId` is an
 * opaque identifier instead of a MAC address.
 */

export type FoundClock = { deviceId: string; name: string; rssi: number };

export function runClockBleAvailable(): boolean {
  if (isNative()) return true;
  return typeof navigator !== "undefined" && "bluetooth" in navigator;
}

let initialised = false;

/**
 * The Bluetooth permission was refused. On iOS the OS asks exactly once; after
 * a refusal the only way back is the app's page in Settings.
 */
export class BluetoothPermissionError extends Error {
  constructor() {
    super("PB Ranks needs Bluetooth permission to find your Run Clock. Allow it in Settings and try again.");
    this.name = "BluetoothPermissionError";
  }
}

/** Open the app's page in the system Settings (native only). */
export async function openBluetoothSettings(): Promise<void> {
  if (!isNative()) return;
  try {
    await BleClient.openAppSettings();
  } catch {
    /* unavailable */
  }
}

/** Initialise the plugin and make sure Bluetooth is on. Throws a readable error otherwise. */
export async function ensureBluetooth(): Promise<void> {
  if (!initialised) {
    try {
      // Android 12+: scanning with neverForLocation needs no location permission.
      await BleClient.initialize({ androidNeverForLocation: true });
    } catch (err) {
      if (/permission|denied|unauthori[sz]ed/i.test(err instanceof Error ? err.message : String(err))) {
        throw new BluetoothPermissionError();
      }
      throw err;
    }
    initialised = true;
  }
  if (!isNative()) return;
  if (platform() === "android") {
    if (!(await BleClient.isEnabled())) {
      try {
        await BleClient.requestEnable();
      } catch {
        /* fall through to the check below */
      }
    }
    if (!(await BleClient.isEnabled())) throw new Error("Turn on Bluetooth to find your Run Clock.");
  } else if (!(await BleClient.isEnabled())) {
    // iOS: no requestEnable; the user flips it in Control Centre.
    throw new Error("Turn on Bluetooth in Control Centre to find your Run Clock.");
  }
}

/** Scan for clocks advertising the provisioning service; returns a stop function. */
export async function scanForClocks(onFound: (clock: FoundClock) => void): Promise<() => Promise<void>> {
  await ensureBluetooth();
  if (isNative()) {
    await BleClient.requestLEScan({ services: [RUN_CLOCK_SERVICE], allowDuplicates: false }, (r: ScanResult) => {
      onFound({ deviceId: r.device.deviceId, name: r.localName ?? r.device.name ?? "", rssi: r.rssi ?? 0 });
    });
    return () => BleClient.stopLEScan();
  }
  // Web Bluetooth has no passive scan: the browser shows its own chooser.
  const device = await BleClient.requestDevice({ services: [RUN_CLOCK_SERVICE] });
  onFound({ deviceId: device.deviceId, name: device.name ?? "", rssi: 0 });
  return async () => {};
}

/**
 * Connect and establish the authenticated link. On Android this raises the
 * system "enter the PIN shown on the device" dialog via bonding; on iOS and
 * the web the first encrypted read (readNetworks) triggers it.
 */
export async function connectClock(deviceId: string, onDisconnect: () => void): Promise<void> {
  await BleClient.connect(deviceId, () => onDisconnect(), { timeout: 15_000 });
  if (isNative() && platform() === "android") {
    await BleClient.createBond(deviceId, { timeout: 60_000 });
  }
}

export async function readInfo(deviceId: string): Promise<RunClockInfo | null> {
  return parseInfo(dataViewToText(await BleClient.read(deviceId, RUN_CLOCK_SERVICE, RUN_CLOCK_CHAR_INFO)));
}

export async function readNetworks(deviceId: string): Promise<string[]> {
  return parseNets(
    dataViewToText(await BleClient.read(deviceId, RUN_CLOCK_SERVICE, RUN_CLOCK_CHAR_NETS, { timeout: 20_000 })),
  );
}

export async function readStatus(deviceId: string): Promise<RunClockStatus | null> {
  return parseStatus(dataViewToText(await BleClient.read(deviceId, RUN_CLOCK_SERVICE, RUN_CLOCK_CHAR_STATUS)));
}

export async function sendCredentials(
  deviceId: string,
  ssid: string,
  password: string,
  provisionedDeviceId?: string,
): Promise<void> {
  await BleClient.write(
    deviceId,
    RUN_CLOCK_SERVICE,
    RUN_CLOCK_CHAR_CREDS,
    textToDataView(credentialsPayload(ssid, password, provisionedDeviceId)),
    { timeout: 15_000 },
  );
}

/** Subscribe to status changes; returns an unsubscribe. */
export async function watchStatus(
  deviceId: string,
  onStatus: (status: RunClockStatus) => void,
): Promise<() => Promise<void>> {
  await BleClient.startNotifications(deviceId, RUN_CLOCK_SERVICE, RUN_CLOCK_CHAR_STATUS, (value) => {
    const s = parseStatus(dataViewToText(value));
    if (s) onStatus(s);
  });
  return () => BleClient.stopNotifications(deviceId, RUN_CLOCK_SERVICE, RUN_CLOCK_CHAR_STATUS).catch(() => {});
}

export async function disconnectClock(deviceId: string): Promise<void> {
  try {
    await BleClient.disconnect(deviceId);
  } catch {
    /* already gone */
  }
}
